import { HttpParams } from '@angular/common/http';
import { sha256 } from 'js-sha256';
import { isObject, get } from 'lodash';
import { isMoment } from 'moment';
import { FormControl } from '@angular/forms';
import {
    FilterOperator,
    CustomHttpParameterCodex,
    FilterObjectConfig,
    FilterDataType,
    FilterObjectBuilder
} from '@eightcap/core';
import { Params } from '@angular/router';
import * as moment from 'moment';
import { ActionRole, AdminModule, ModuleAction, ModulePermission } from '@modules/auth/models/user-auth.model';
import { SessionService } from '@core/services/session.service';
import { TenantName } from '@shared/models/tenant-name';
import { Role } from '@shared/models/role';
import { CompanyGroup } from '@shared/models/company-group';
import { timer } from 'rxjs';
export default class Helper {
    static parseParams(options): HttpParams {
        let httpParams = new HttpParams({
            encoder: new CustomHttpParameterCodex()
        });
        // tslint:disable-next-line: typedef
        Object.keys(options).forEach(function (key) {
            httpParams = httpParams.append(key, options[key]);
        });
        return httpParams;
    }

    static isEmpty(obj): boolean {
        for (const key in obj) {
            // tslint:disable-next-line: curly
            if (obj.hasOwnProperty(key)) return false;
        }
        return true;
    }

    static formatPostParams(params): any {
        return { data: params };
    }

    static generateSecret(password: string, email: string): string {
        return sha256(password + email);
    }

    static generateKey(secret: any, unixTimeStamp: any, ipAddress?: string): string {
        return sha256(secret + unixTimeStamp + ipAddress);
    }

    static flattenObject(obj: any): any {
        const keys = Helper.flattenKeys(obj);
        const flattenObj = {};
        keys.forEach((key: string) => {
            flattenObj[key] = get(obj, key);
        });

        return flattenObj;
    }

    static flattenKeys(obj: any): string[] {
        const flattened = [];
        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (isObject(value) && !isMoment(value)) {
                const internalKeys = Helper.flattenKeys(value);
                internalKeys.forEach((internalKey: string) => {
                    flattened.push(`${key}.${internalKey}`);
                });
            } else {
                flattened.push(key);
            }
        });
        return flattened;
    }

    private static makeNewKey(keyProp: string, itr: number, sep: string, obj: any): string {
        const newKey = [keyProp, sep, itr].join('');
        if (Object.keys(obj).indexOf(newKey) === -1) {
            return newKey;
        } else {
            return Helper.makeNewKey(keyProp, ++itr, sep, obj);
        }
    }

    static getLastThirtyDay(): string {
        return new Date(new Date().setDate(new Date().getDate() - 30)).toISOString();
    }

    static setDefaultDatePicker(date: string): any {
        return new FormControl(date);
    }

    static convertFilterParams(filterParams: string, filterConfigs: Array<FilterObjectConfig>): Params {
        const paramsArr = filterParams.split('&');
        return paramsArr
            .filter((item, pos) => paramsArr.indexOf(item) === pos)
            .reduce((obj, item) => {
                const filter = this.extractKeyValue(item);
                this.adjustFilterByConfig(filter, filterConfigs);

                return Object.assign(obj, {
                    [filter.key]:
                        filter.value === 'false' || filter.value === 'true' ? JSON.parse(filter.value) : filter.value
                });
            }, {});
    }

    private static adjustFilterByConfig(filter: Params, filterConfigs: Array<FilterObjectConfig>): void {
        const config = filterConfigs.find(
            (it) => it.field === filter.key || it.field === filter.key + '_from' || it.field === filter.key + '_to'
        );

        if (config && config.dataType === FilterDataType.dateRange) {
            if (filter.operator === FilterOperator.gte) {
                filter.key += '_from';
            } else if (filter.operator === FilterOperator.lte) {
                filter.key += '_to';
            }

            filter.value = moment(filter.value.substring(0, 8));
        }

        if (config?.dataType === FilterDataType.date) {
            filter.value = moment(filter.value.substring(0, 8));
        }
    }

    private static extractKeyValue(item: string): { [k: string]: string } {
        const operator = Object.keys(FilterOperator).find(
            (key) => item.indexOf(`[${FilterOperator[key as any]}]=`) > -1
        );
        const extracted = item.split(`[${operator}]=`);
        if (operator === FilterOperator.like) {
            extracted[1] = extracted[1].slice(1, -1);
        }
        if (operator === FilterOperator.in) {
            const splitExtracted = extracted[1].split(',');
            extracted[1] = splitExtracted as unknown as string;
        }
        return { key: extracted[0], value: extracted[1], operator };
    }

    static buildBackLinkQueryParams(filters: Params): Params {
        return {
            ...new FilterObjectBuilder().byObject(filters, FilterOperator.eq).apiFilter(),
            backLink: 1
        };
    }
    static getListPermission(sessionService: SessionService, moduleName: string): ModulePermission[] {
        return sessionService.authenticatedUser.permissions.filter(
            (res) => res.module === moduleName || res.module === AdminModule.All
        );
    }
    static getRoleList(listAction: ModuleAction[]): ActionRole {
        return {
            can_create: listAction.includes(ModuleAction.Create) || listAction.includes(ModuleAction.All),
            can_view: listAction.includes(ModuleAction.View) || listAction.includes(ModuleAction.All),
            can_update: listAction.includes(ModuleAction.Update) || listAction.includes(ModuleAction.All),
            can_delete: listAction.includes(ModuleAction.Delete) || listAction.includes(ModuleAction.All),
            can_export_excel: listAction.includes(ModuleAction.Export) || listAction.includes(ModuleAction.All),
            can_approve: listAction.includes(ModuleAction.Approve) || listAction.includes(ModuleAction.All)
        };
    }
    static filterTenant(role: string, tenantList: TenantName[]): TenantName[] {
        switch (role) {
            case Role.ADMIN_ALL:
                return tenantList;
            case Role.ADMIN_EIG:
                return tenantList.filter((e: TenantName) => {
                return e.substr(0, 3) === CompanyGroup.EIG;
                    });
            case Role.ADMIN_BLU:
                return tenantList.filter((e: TenantName) => {
                    return e.substr(0, 3) === CompanyGroup.BLU;
                });
            case Role.ADMIN_EIG_ASIC:
                return tenantList.filter((e: TenantName) => {
                    return e === TenantName.EIG_ASIC;
                });
        }
    }

    static focusContent(id: string): void {
        const elementFirst: HTMLElement = document.querySelector(`#${id}`) as HTMLElement;
        if (elementFirst) {
            timer(150).subscribe(() => {
                elementFirst.focus();
                elementFirst.scrollIntoView({ behavior: 'smooth' });
            });
        }
    }
}
